/* @flow */

import shaka from 'shaka-player';

import type { BasicFunction, KeyValuePair } from '../../../helpers/jsHelpers/types';

import { MILLISECONDS_PER_SECOND } from '../../../helpers/dateTime/Format';
import { logError, logInfo } from '../../../helpers/debug/debug';
import SentryWrapper from '../../../helpers/debug/sentry';
import { SentryTagName, SentryTagValue } from '../../../helpers/debug/sentryTypes';
import { DRM_KEY_SYSTEM, Drm } from '../../../helpers/jsHelpers/Drm';
import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import { getBoundedValue } from '../../../helpers/maths/maths';
import { MediametrieState } from '../../../helpers/mediametrie/types';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { PlayerError } from '../../../helpers/player/PlayerError';
import { CAPTIONS, SUBTITLES, isHearingImpairedSubtitlesTrack } from '../../../helpers/ui/language';
import { HeaderName, HeaderValue } from '../../../libs/netgemLibrary/v8/constants/Headers';
import { HttpStatus } from '../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { ContentType } from '../constantsAndTypes';
import { type UnifiedBufferingObserver, createUnifiedBufferingObserver } from './bufferingObserver';
import { cloneAllTracks, generateUpdateDownloadProgress, getVerimatrixContentId, selectDownloadTracks } from './shakaHelpers';
import ShakaStorage from './shakaStorage';
import {
  type AllTracks,
  type AudioTrack,
  type IndexedShakaTrack,
  type ShakaAudioLanguageRole,
  type ShakaConfiguration,
  type ShakaError,
  ShakaErrors,
  type ShakaOfflineContent,
  type ShakaOfflineExtraMetadata,
  type ShakaPlayerSettings,
  type ShakaTrack,
  type ShakaTrackList,
  type VideoTrack,
} from './shakaTypes';
import {
  HtmlMediaError,
  VIDEOPLAYER_ERRORS,
  VIDEOPLAYER_NAME,
  type VideoPlayerBitrateInfo,
  type VideoPlayerExternalSubtitles,
  type VideoPlayerInitData,
  type VideoPlayerLiveMetrics,
  type VideoPlayerMediaInfo,
  VideoPlayerMediaType,
} from './types';

const ONE_MILLION = 1_000_000;

// If an error occurred during media decoding on Safari (Fairplay), retry up to MAX_RETRIES with an increasing delay between attempts
const MAX_RETRIES = 3;

// Wait 1s before attempt #2, then 2s before attempt #3, then 3s, etc. (in ms)
const RETRY_DELAY = 1_000;

// Timeshift capabilities lower than 300 s (5 min) is considered as no timeshift at all
const LIVE_TIMESHIFT_BUFFER_DEPTH_THRESHOLD = 300;

// Default max attempts in case of error
const DEFAULT_MAX_ATTEMPTS = 6;

// When playing start over items, duration retrieved by player is huge and incorrect
const INFINITY_LIKE = 1e9;

class PlayerShaka {
  authenticationToken: string | null;

  bufferingCallback: BasicFunction;

  bufferLoadedCallback: BasicFunction;

  bufferingObserver: UnifiedBufferingObserver;

  canUpdateStartTimeCallback: BasicFunction;

  certificate: ArrayBuffer;

  contentType: ContentType;

  duration: number;

  endPosition: ?number;

  errorCallback: (category: number, code: number, mediaErrorCode: number, errorMsg?: string, isForbidden?: boolean) => Promise<void> | void;

  hasVUDrmToken: boolean;

  initData: VideoPlayerInitData | null;

  isStopped: boolean;

  isVerimatrixFairplay: boolean;

  isVod: boolean;

  liveBufferLengthUpdatedCallback: (liveBufferLength: number) => void;

  name: string = VIDEOPLAYER_NAME;

  playbackEndedCallback: () => Promise<void> | void;

  playbackPausedCallback: BasicFunction;

  playbackPlayingCallback: BasicFunction;

  playbackTimeUpdatedCallback: (time: number, timeshift?: number) => void;

  retryCount: number;

  retryTimer: ?TimeoutID;

  // Id of selected audio track
  selectedAudioId: number | null;

  // Id of selected text track
  selectedTextId: number | null;

  // Id of selected video track
  selectedVideoId: number | null;

  settings: ShakaPlayerSettings;

  shakaPlayer: typeof shaka.Player | null;

  skdUri: string;

  streamInfoUpdatedCallback: BasicFunction;

  streamInitializedCallback: (duration: number) => void;

  title: string;

  tracks: AllTracks;

  videoContainer: HTMLElement | null;

  videoElement: HTMLVideoElement;

  volumeChangedCallback: (volume: number) => void;

  constructor(
    videoElement: HTMLVideoElement,
    videoContainer: HTMLElement | null,
    settings: ShakaPlayerSettings,
    authenticationToken: string | null,
    isVod: boolean,
    contentType: ContentType,
    title: string,
  ) {
    this.authenticationToken = authenticationToken;
    this.contentType = contentType;
    this.duration = NaN;
    this.endPosition = null;
    this.hasVUDrmToken = false;
    this.initData = null;
    this.isStopped = true;
    this.isVerimatrixFairplay = false;
    this.isVod = isVod;
    this.retryCount = 0;
    this.retryTimer = null;
    this.selectedAudioId = null;
    this.selectedTextId = null;
    this.selectedVideoId = null;
    this.settings = settings;
    this.shakaPlayer = null;
    this.skdUri = '';
    this.title = title;
    this.tracks = {
      audio: [],
      text: [],
      video: [],
    };
    this.videoElement = videoElement;
    this.videoContainer = videoContainer;

    // Install built-in polyfills to patch browser incompatibilities
    shaka.polyfill.installAll();

    /*
     * To debug Shaka Player, replace the import by:
     * import shaka from 'shaka-player/dist/shaka-player.compiled.debug';
     *
     * and set the desired log level (DEBUG, V1 or V2):
     * shaka.log.setLevel(shaka.log.Level.V2);
     */

    // Check to see if the browser supports the basic APIs Shaka needs
    if (!shaka.Player.isBrowserSupported()) {
      throw new Error('BrowserNotSupported');
    }
  }

  showDebug: () => void = () => {
    const { shakaPlayer } = this;

    if (shakaPlayer) {
      logInfo('---------- Shaka Player configuration');
      logInfo(shakaPlayer.getNonDefaultConfiguration());
      logInfo(`Presentation delay: ${shakaPlayer.getManifest()?.presentationTimeline.getDelay() ?? 'n/a'}`);
    } else {
      logInfo('Shaka Player not initialized');
    }
  };

  updateMaxBitrate: (maxBitrate: number) => boolean = (maxBitrate) => {
    const { shakaPlayer } = this;

    return shakaPlayer?.configure('restrictions.maxBandwidth', maxBitrate === -1 ? Infinity : maxBitrate * ONE_MILLION) ?? false;
  };

  getName: () => string = () => this.name;

  getVersion: () => string = () => shaka.Player.version;

  getState: () => MediametrieState = () => {
    const { videoElement } = this;

    if (videoElement.ended) {
      return MediametrieState.Stop;
    }

    return videoElement.paused ? MediametrieState.Pause : MediametrieState.Play;
  };

  // Only used for Mediametrie, return position in whole seconds
  getPosition: () => number = () => {
    const { contentType, endPosition, shakaPlayer, videoElement } = this;

    if (!shakaPlayer) {
      return 0;
    }

    const getPositionInternal = () => {
      if (videoElement.ended) {
        // Playhead reached end of stream
        return endPosition ?? 0;
      }

      if (contentType === ContentType.Static) {
        return videoElement.currentTime;
      }

      // Property contentType cannot be LiveRecording here since Mediametrie is disabled for recordings, so the last case is Live
      return (shakaPlayer.getPlayheadTimeAsDate()?.getTime() ?? 0) / MILLISECONDS_PER_SECOND;
    };

    return Math.round(getPositionInternal());
  };

  initialize: (initData: VideoPlayerInitData, startPlaying: boolean) => Promise<void> = async (initData, startPlaying) => {
    await this.reset();

    const { videoContainer, videoElement } = this;
    const { fairplayCertificateUrl, fairplayContentKeySystem, laUrl } = initData;

    this.isVerimatrixFairplay = fairplayContentKeySystem === 'verimatrix';

    if (this.isVerimatrixFairplay) {
      shaka.polyfill.PatchedMediaKeysApple.install(true);
    }

    this.shakaPlayer = new shaka.Player();
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      throw new Error('Cannot initialize Shaka Player');
    }

    this.initData = initData;

    if (videoContainer) {
      // Provide video container (for subtitles)
      shakaPlayer.setVideoContainer(videoContainer);
    }

    // Add callbacks
    this.plugCallbacks(shakaPlayer, videoElement);

    try {
      // Attach HTML element
      await shakaPlayer.attach(videoElement);

      if (fairplayCertificateUrl && laUrl) {
        // Fairplay DRM (hence Safari)
        const certificate = await this.loadCertificate(fairplayCertificateUrl);
        if (certificate) {
          this.certificate = certificate;
        }
        return this.configureAndStart(shakaPlayer, initData, startPlaying);
      }

      // Widevine, Playready or no DRM
      return this.configureAndStart(shakaPlayer, initData, startPlaying);
    } catch (error) {
      logInfo('Error initializing Shaka Player');
      throw error;
    }
  };

  initializeOffline: () => Promise<void> = async () => {
    const { videoContainer, videoElement } = this;

    this.shakaPlayer = new shaka.Player();
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      throw new Error('Cannot initialize Shaka Player');
    }

    if (videoContainer) {
      // Provide video container (for subtitles)
      shakaPlayer.setVideoContainer(videoContainer);
    }

    // Add callbacks
    this.plugCallbacks(shakaPlayer, videoElement);

    try {
      // Attach HTML element
      await shakaPlayer.attach(videoElement);

      /*
       *  TODO:
       *  if (fairplayCertificateUrl && laUrl) {
       *    // Fairplay DRM (hence Safari)
       *    const certificate = await this.loadCertificate(fairplayCertificateUrl);
       *    if (certificate) {
       *      this.certificate = certificate;
       *    }
       *    return this.configureAndStart(shakaPlayer, initData);
       *  }
       *
       * // Widevine, Playready or no DRM
       * return this.configureAndStart(shakaPlayer, initData);
       */
    } catch (error) {
      logInfo('Error attaching videoElement');
      throw error;
    }
  };

  startOffline: (content: ShakaOfflineContent) => Promise<void> = async (content) => {
    const { shakaPlayer, videoElement } = this;
    const { offlineUri } = content;

    if (!shakaPlayer) {
      throw new PlayerError(VIDEOPLAYER_ERRORS.VS994OfflineContentError);
    }

    if (await ShakaStorage.loadContent(shakaPlayer, offlineUri)) {
      return videoElement.play();
    }
  };

  configureAndStart: (shakaPlayer: typeof shaka.Player, initData: VideoPlayerInitData, startPlaying: boolean) => Promise<void> = (shakaPlayer, initData, startPlaying) => {
    this.configurePlayer(shakaPlayer, initData);

    // Loads manifest and play
    return this.start(shakaPlayer, initData, startPlaying);
  };

  start: (shakaPlayer: typeof shaka.Player, initData: VideoPlayerInitData, startPlaying: boolean) => Promise<void> = async (shakaPlayer, initData, startPlaying) => {
    const { videoElement } = this;
    const { url } = initData;

    try {
      await shakaPlayer.load(url);

      // Handle buffering event specifically because of Safari
      this.bufferingObserver = createUnifiedBufferingObserver(shakaPlayer, videoElement);

      // Force reset
      this.bufferLoadedCallback();

      this.bufferingObserver.initialize();
      this.bufferingObserver.onChange((isBuffering: boolean) => {
        if (isBuffering) {
          this.bufferingCallback();
        } else {
          this.bufferLoadedCallback();
        }
      });

      this.initializeAudioAndVideoTracks();

      if (startPlaying) {
        await videoElement.play();
      } else {
        return Promise.resolve();
      }
    } catch (error) {
      logInfo(`Error in play() promise (${error.message})`);
      throw error;
    }
  };

  loadCertificate: (fairplayCertificateUrl: string) => Promise<?ArrayBuffer> = async (fairplayCertificateUrl) => {
    const response = await fetch(fairplayCertificateUrl);
    return response.arrayBuffer();
  };

  plugCallbacks: (shakaPlayer: typeof shaka.Player, videoElement: HTMLElement) => void = (shakaPlayer, videoElement) => {
    shakaPlayer.addEventListener('error', this.errorEvent, { passive: true });
    shakaPlayer.addEventListener('textchanged', this.textChangedEvent, { passive: true });
    shakaPlayer.addEventListener('trackschanged', this.tracksChangedEvent, { passive: true });
    shakaPlayer.addEventListener('adaptation', this.adaptationEvent, { passive: true });
    shakaPlayer.addEventListener('variantchanged', this.variantChangedEvent, { passive: true });
    shakaPlayer.addEventListener('canupdatestarttime', this.canUpdateStartTimeEvent, { passive: true });

    videoElement.addEventListener('ended', this.endedEvent, { passive: true });
    videoElement.addEventListener('pause', this.pausedEvent, { passive: true });
    videoElement.addEventListener('playing', this.playingEvent, { passive: true });
    videoElement.addEventListener('timeupdate', this.timeUpdateEvent, { passive: true });
    videoElement.addEventListener('durationchange', this.durationChangeEvent, { passive: true });
    videoElement.addEventListener('volumechange', this.volumeChangedEvent, { passive: true });
  };

  configurePlayer: (shakaPlayer: typeof shaka.Player, initData: VideoPlayerInitData) => boolean = (shakaPlayer, initData) => {
    const {
      settings: { bufferBehind, bufferingGoal, rebufferingGoal },
    } = this;
    const { audioLanguage, maxBitrate, subtitlesLanguage } = initData;

    const configuration: ShakaConfiguration = {
      streaming: {
        bufferBehind,
        bufferingGoal,
        ignoreTextStreamFailures: true,
        lowLatencyMode: true,
        rebufferingGoal,
        retryParameters: { maxAttempts: DEFAULT_MAX_ATTEMPTS },
      },
    };

    // DRM
    this.addDrmConfiguration(initData, configuration);

    // Auto-select audio track
    if (audioLanguage) {
      configuration.preferredAudio = [{ language: audioLanguage }];
    }

    // Auto-select subtitles track
    if (subtitlesLanguage) {
      configuration.preferredText = [{ language: subtitlesLanguage }];
    }

    // Green streaming
    if (typeof maxBitrate === 'number') {
      configuration.restrictions = { maxBandwidth: maxBitrate === -1 ? Infinity : maxBitrate * ONE_MILLION };
    }

    return shakaPlayer.configure(configuration);
  };

  resetRetryTimer: () => void = () => {
    const { retryTimer } = this;

    if (retryTimer) {
      clearTimeout(retryTimer);
      this.retryTimer = null;
    }
  };

  reset: () => Promise<void> = async () => {
    const { initData, shakaPlayer, videoElement } = this;

    const isSafariFairPlay = initData?.drm === Drm.Fairplay;

    this.resetRetryTimer();

    this.endPosition = null;
    this.initData = null;
    this.retryCount = 0;
    this.skdUri = '';

    if (this.isVerimatrixFairplay) {
      shaka.polyfill.PatchedMediaKeysApple.uninstall();
    }

    videoElement.removeEventListener('ended', this.endedEvent, { passive: true });
    videoElement.removeEventListener('pause', this.pausedEvent, { passive: true });
    videoElement.removeEventListener('playing', this.playingEvent, { passive: true });
    videoElement.removeEventListener('timeupdate', this.timeUpdateEvent, { passive: true });
    videoElement.removeEventListener('durationchange', this.durationChangeEvent, { passive: true });
    videoElement.removeEventListener('volumechange', this.volumeChangedEvent, { passive: true });

    this.bufferingObserver?.destroy();

    if (!shakaPlayer) {
      return;
    }

    shakaPlayer.removeEventListener('error', this.errorEvent, { passive: true });
    shakaPlayer.removeEventListener('textchanged', this.textChangedEvent, { passive: true });
    shakaPlayer.removeEventListener('trackschanged', this.tracksChangedEvent, { passive: true });
    shakaPlayer.removeEventListener('adaptation', this.adaptationEvent, { passive: true });
    shakaPlayer.removeEventListener('variantchanged', this.variantChangedEvent, { passive: true });
    shakaPlayer.removeEventListener('canupdatestarttime', this.canUpdateStartTimeEvent, { passive: true });

    try {
      await shakaPlayer.detach();
      await shakaPlayer.destroy();
      delete this.shakaPlayer;
    } catch (error) {
      logError('Error detaching videoElement or destroying player');
      logError(error);
    }

    if (isSafariFairPlay) {
      // Fixes bug GM-6589
      const oldVideoElement = this.videoElement;
      const newVideoElement = oldVideoElement.cloneNode(true);
      newVideoElement.classList.remove('shifted');
      oldVideoElement.replaceWith(newVideoElement);
      this.videoElement = newVideoElement;
    }
  };

  configureRequestFilter: (shakaPlayer: typeof shaka.Player, initData: VideoPlayerInitData) => void = (shakaPlayer, initData) => {
    const { customHeaders, drm, vuDrmToken } = initData;

    if (!customHeaders && !vuDrmToken && drm !== Drm.Fairplay) {
      return;
    }

    shakaPlayer.getNetworkingEngine().registerRequestFilter((type, request) => {
      if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) {
        return;
      }

      if (customHeaders) {
        request.headers = customHeaders;
      }

      if (vuDrmToken) {
        // FTV special Dash/Widevine case
        this.hasVUDrmToken = true;
        request.headers[HeaderName.VuDrmToken] = vuDrmToken;
      }

      // Our entitlement server needs a JSON body, plus authorization header
      if (drm === Drm.Fairplay) {
        // Add auth token in headers and build payload
        const skd = this.skdUri;

        if (skd === '') {
          logError('Missing skdUri → dropping request');
          return;
        }

        request.headers[HeaderName.ContentType] = HeaderValue.ApplicationJson;
        request.headers[HeaderName.Authorization] = `Bearer ${this.authenticationToken ?? ''}`;
        const spc = shaka.util.Uint8ArrayUtils.toStandardBase64(request.body);

        request.body = shaka.util.StringUtils.toUTF8(
          JSON.stringify({
            skd,
            spc,
          }),
        );
      }
    });
  };

  configureResponseFilter: (shakaPlayer: typeof shaka.Player, initData: VideoPlayerInitData) => void = (shakaPlayer, initData) => {
    const { drm } = initData;

    if (drm !== Drm.Fairplay) {
      return;
    }

    // Our entitlement server sends back the ckc inside a JSON object
    shakaPlayer.getNetworkingEngine().registerResponseFilter((type, response) => {
      if (type !== shaka.net.NetworkingEngine.RequestType.LICENSE) {
        return;
      }

      const { ckc } = JSON.parse(shaka.util.StringUtils.fromUTF8(response.data));
      response.data = shaka.util.Uint8ArrayUtils.fromBase64(ckc).buffer;
    });
  };

  configureServers: (configuration: any, drm: Drm, serverUrl: string) => void = (configuration, drm, serverUrl) => {
    const { certificate } = this;

    switch (drm) {
      case Drm.Fairplay:
        configuration.drm = {
          advanced: { [DRM_KEY_SYSTEM.Fairplay]: { serverCertificate: new Uint8Array(certificate) } },
          initDataTransform: (initData, initDataType, drmInfo) => {
            if (initDataType !== 'skd') {
              return initData;
            }

            /*
             * Variable 'initData' is a buffer containing an 'skd://' URL as a UTF-8 string
             * It's stored for future use when building the entitlement request (to retrieve the CKC)
             */
            this.skdUri = shaka.util.StringUtils.fromBytesAutoDetect(initData);

            if (!this.isVerimatrixFairplay) {
              // Regular case: use SKD as it is and return untouched initData
              return initData;
            }

            // Verimatrix case: return initData with SKD URI replaced by content Id
            const contentId = getVerimatrixContentId(this.skdUri);
            return shaka.drm.FairPlay.initDataTransform(initData, contentId, drmInfo.serverCertificate);
          },
          servers: { [DRM_KEY_SYSTEM.Fairplay]: serverUrl },
        };
        break;

      case Drm.Playready:
        configuration.drm = { servers: { [DRM_KEY_SYSTEM.Playready as string]: serverUrl } };
        break;

      case Drm.Widevine:
        configuration.drm = {
          advanced: {
            [DRM_KEY_SYSTEM.Widevine as string]: {
              audioRobustness: ['SW_SECURE_CRYPTO'],
              videoRobustness: ['SW_SECURE_CRYPTO'],
            },
          },
          servers: { [DRM_KEY_SYSTEM.Widevine as string]: serverUrl },
        };

      // No default
    }
  };

  addDrmConfiguration: (initData: VideoPlayerInitData, configuration: ShakaConfiguration) => void = (initData, configuration) => {
    const { drm, laUrl } = initData;
    const { shakaPlayer } = this;

    if (!shakaPlayer || drm === undefined) {
      return;
    }

    if (!laUrl) {
      logError('DRM without laUrl');
      return;
    }

    const serverUrl = laUrl.replace(/^http:/iu, 'https:');

    this.configureRequestFilter(shakaPlayer, initData);
    this.configureResponseFilter(shakaPlayer, initData);
    this.configureServers(configuration, drm, serverUrl);
  };

  compareNumbers: (a: ?number, b: ?number) => number | null = (a, b) => {
    if (typeof a !== 'number' && typeof b !== 'number') {
      return null;
    }

    if (typeof a !== 'number') {
      return 1;
    }

    if (typeof b !== 'number') {
      return -1;
    }

    return a - b;
  };

  sortAudioTrack: (a: AudioTrack, b: AudioTrack) => number = (a, b) => {
    const result = this.compareNumbers(a.id, b.id);
    if (typeof result === 'number') {
      return result;
    }

    return 0;
  };

  sortVideoTrack: (a: VideoTrack, b: VideoTrack) => number = (a, b) => {
    let result = this.compareNumbers(a.height, b.height);
    if (typeof result === 'number') {
      return result;
    }

    result = this.compareNumbers(a.videoBandwidth, b.videoBandwidth);
    if (typeof result === 'number') {
      return result;
    }

    result = this.compareNumbers(a.id, b.id);
    if (typeof result === 'number') {
      return result;
    }

    return 0;
  };

  getInitializeTrack: (audioTracks: { [number]: AudioTrack }, videoTracks: { [number]: VideoTrack }) => (track: ShakaTrack) => void = (audioTracks, videoTracks) => (track) => {
    const {
      active,
      audioBandwidth,
      audioCodec,
      audioId,
      audioMimeType,
      bandwidth,
      frameRate,
      height,
      id,
      label,
      language,
      mimeType,
      originalAudioId,
      originalVideoId,
      roles,
      type,
      videoBandwidth,
      videoCodec,
      videoId,
      videoMimeType,
      width,
    } = track;

    if (type === 'variant') {
      // Audio/video track
      if (active) {
        this.selectedAudioId = audioId;
        this.selectedVideoId = videoId;
      }

      if (audioId && !(audioId in audioTracks)) {
        audioTracks[audioId] = {
          audioBandwidth,
          audioCodec,
          audioId,
          audioMimeType,
          bandwidth,
          id,
          label,
          language,
          mimeType,
          originalAudioId,
          roles,
        };
      }

      if (videoId && !(videoId in videoTracks)) {
        videoTracks[videoId] = {
          bandwidth,
          frameRate,
          height,
          id,
          label,
          language,
          mimeType,
          originalVideoId,
          roles,
          videoBandwidth,
          videoCodec,
          videoId,
          videoMimeType,
          width,
        };
      }
    }
  };

  initializeAudioAndVideoTracks: () => void = () => {
    const { shakaPlayer } = this;
    const audioTracks: { [number]: AudioTrack } = {};
    const videoTracks: { [number]: VideoTrack } = {};

    const initializeTrack = this.getInitializeTrack(audioTracks, videoTracks);

    shakaPlayer?.getVariantTracks().forEach(initializeTrack);

    this.tracks.audio = Object.values(audioTracks).sort(this.sortAudioTrack);
    this.tracks.video = Object.values(videoTracks).sort(this.sortVideoTrack);
  };

  // Adds external subtitles if and only if the same subtitles have not already been loaded from manifest
  addSubtitles: (subtitles: Array<VideoPlayerExternalSubtitles>) => Promise<void> = async (subtitles) => {
    const { shakaPlayer, streamInfoUpdatedCallback } = this;

    if (!shakaPlayer) {
      return;
    }

    // Build a lookup map of subtitles found in manifest
    const subtitlesMap: KeyValuePair<Set<string>> = {};
    shakaPlayer.getTextTracks().forEach((track) => {
      let { language: subsForLang } = subtitlesMap;

      if (!subsForLang) {
        const { language } = track;

        subsForLang = new Set<string>();
        subtitlesMap[language] = subsForLang;
      }

      subsForLang.add(isHearingImpairedSubtitlesTrack(track) ? CAPTIONS : SUBTITLES);
    });

    const promises: Array<Promise<void>> = [];

    subtitles.forEach((track) => {
      const { captions, language, mimeType, url } = track;

      if (!subtitlesMap[language]?.has(captions ? CAPTIONS : SUBTITLES)) {
        // External subtitles track not present in manifest
        const promise = shakaPlayer.addTextTrackAsync(url, language, captions ? CAPTIONS : SUBTITLES, mimeType).catch((error) => {
          SentryWrapper.error({
            breadcrumbs: ['Shaka Player', 'addSubtitles'],
            context: {
              language,
              mimeType,
              url,
            },
            error,
            tagName: SentryTagName.Component,
            tagValue: SentryTagValue.Player,
          });
        });

        promises.push(promise);
      }
    });

    await Promise.allSettled(promises);

    // Update Player instance so that it's aware of the new subtitles
    streamInfoUpdatedCallback();
  };

  /*
   * Fired when a playback error occurs
   */
  errorEvent: (error: ShakaError) => void = (error) => {
    const { errorCallback, isVod, retryCount } = this;
    const {
      detail,
      detail: {
        category,
        code,
        data: [untypedMediaErrorCode, httpStatus, untypedErrorMsg],
      },
    } = error;

    const mediaErrorCode = typeof untypedMediaErrorCode === 'number' ? untypedMediaErrorCode : 0;

    logError(detail);

    if (
      // Shaka error category and code
      category === shaka.util.Error.Category.MEDIA &&
      code === shaka.util.Error.Code.VIDEO_ERROR &&
      // HTML MEDIA_ERR_DECODE
      mediaErrorCode === (HtmlMediaError.Decode as number) &&
      // Fairplay DRM
      this.skdUri !== '' &&
      // Not too many retries
      retryCount < MAX_RETRIES &&
      // Not VOD
      !isVod
    ) {
      // MEDIA_ERR_DECODE while decoding Fairplay content on Safari: hide error and retry a few times without exiting player
      this.resetRetryTimer();
      this.retryCount += 1;
      const timeout = RETRY_DELAY * this.retryCount;
      logInfo(`Fairplay decode error: retry ${this.retryCount}/${MAX_RETRIES} in ${timeout / MILLISECONDS_PER_SECOND} seconds...`);

      this.retryTimer = setTimeout(() => {
        const { initData, shakaPlayer } = this;

        if (initData && shakaPlayer) {
          logInfo(`Retrying now (${this.retryCount}/${MAX_RETRIES})`);
          fireAndForget(this.start(shakaPlayer, initData, true), 'PlayerShaka.start');
        }
      }, timeout);

      // Hide error
      return;
    }

    errorCallback(category, code, mediaErrorCode, untypedErrorMsg?.toString(), httpStatus === HttpStatus.Forbidden);
  };

  durationChangeEvent: () => void = () => {
    const {
      contentType,
      shakaPlayer,
      streamInitializedCallback,
      videoElement: { duration },
    } = this;

    let value = 0;

    if (shakaPlayer) {
      // Live and LiveRecording contents returns Infinity
      value = contentType === ContentType.Static && duration < INFINITY_LIKE ? duration : Infinity;
    }

    streamInitializedCallback(value);
  };

  /*
   * Return playhead position
   *  - catchup: duration from beginning in seconds
   *  - live:    timestamp in seconds
   */
  timeUpdateEvent: () => void = () => {
    const {
      contentType,
      liveBufferLengthUpdatedCallback,
      playbackTimeUpdatedCallback,
      shakaPlayer,
      videoElement: { currentTime },
    } = this;

    if (!shakaPlayer) {
      return;
    }

    /*
     * It takes a small amount of time to Shaka Player before isLive() actually returns "true".
     * So when we play live content but isLive() returns false, we skip this callback
     */
    if (contentType !== ContentType.Static && !shakaPlayer.isLive()) {
      return;
    }

    if (contentType === ContentType.Static) {
      // Catchup, finished recording, VOD
      playbackTimeUpdatedCallback(currentTime);
    } else {
      // Live (including live recording)
      const { end, start } = shakaPlayer.seekRange();

      // Current time is sometimes (mostly in Safari) greater than the seek range's end, so buffer length is capped to 0
      const liveBufferLength = Math.max(0, end - start);

      if (liveBufferLength >= LIVE_TIMESHIFT_BUFFER_DEPTH_THRESHOLD) {
        liveBufferLengthUpdatedCallback(liveBufferLength);
      }

      // In some rare cases, Shaka cannot tell the playhead position
      let position = (shakaPlayer.getPlayheadTimeAsDate()?.getTime() ?? 0) / MILLISECONDS_PER_SECOND;
      const timeshift = end - currentTime;

      if (contentType === ContentType.LiveRecording) {
        position = currentTime - start;
        if (position < 0) {
          position = 0;
        }
      }

      playbackTimeUpdatedCallback(position, timeshift);
    }
  };

  /*
   * Fired when a call from the application caused a text stream change
   * Can be triggered by calls to selectTextTrack() or selectTextLanguage()
   */
  textChangedEvent: () => void = () => {
    const { shakaPlayer, streamInfoUpdatedCallback } = this;

    if (!shakaPlayer) {
      return;
    }

    this.selectedTextId = this.findSelectedTrackId(shakaPlayer.getTextTracks());
    streamInfoUpdatedCallback();
  };

  /*
   * Fired when the list of tracks changes
   * For example, this will happen when new tracks are added/removed or when track restrictions change
   */
  tracksChangedEvent: () => void = () => {
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      return;
    }

    const textTracks = shakaPlayer.getTextTracks();
    this.tracks.text = textTracks.map((track) => {
      const { forced, id, kind, label, language, mimeType, roles } = track;

      return {
        forced,
        id,
        kind,
        label,
        language,
        mimeType,
        roles,
      };
    });

    this.selectedTextId = this.findSelectedTrackId(textTracks);
  };

  findSelectedTrackId: (tracks: ShakaTrackList) => number | null = (tracks) => tracks.find((track) => track.active)?.id ?? null;

  /*
   * Fired when an automatic adaptation (i.e. ABR manager) causes the active tracks to change
   * Does not fire when the application calls selectVariantTrack(), selectTextTrack(), selectAudioLanguage(), or selectTextLanguage()
   */
  adaptationEvent: () => void = () => {
    this.initializeAudioAndVideoTracks();
  };

  /*
   * Fired when a call from the application (i.e. user action) caused a variant change
   * Can be triggered by calls to selectVariantTrack() or selectAudioLanguage()
   * Does not fire when an automatic adaptation causes a variant change
   */
  variantChangedEvent: () => void = () => {
    this.initializeAudioAndVideoTracks();
  };

  canUpdateStartTimeEvent: () => void = () => {
    const { canUpdateStartTimeCallback } = this;

    canUpdateStartTimeCallback();
  };

  endedEvent: () => void = () => {
    const {
      playbackEndedCallback,
      shakaPlayer,
      videoElement: { currentTime },
    } = this;

    if (!shakaPlayer) {
      return;
    }

    // Catchup, recording or VOD
    this.endPosition = currentTime;

    playbackEndedCallback();
  };

  pausedEvent: () => void = () => {
    const { playbackPausedCallback } = this;

    playbackPausedCallback();
  };

  playingEvent: () => void = () => {
    const { playbackPlayingCallback, streamInfoUpdatedCallback } = this;

    this.retryCount = 0;
    this.resetRetryTimer();

    streamInfoUpdatedCallback();
    playbackPlayingCallback();
  };

  volumeChangedEvent: () => void = () => {
    const { videoElement, volumeChangedCallback } = this;

    volumeChangedCallback(videoElement.volume);
  };

  getBitrateInfoListFor: (mediaType: VideoPlayerMediaType) => Array<VideoPlayerBitrateInfo> = () => [];

  getQualityFor: (mediaType: VideoPlayerMediaType) => number = () => -1;

  getAutoSwitchQualityFor: (mediaType: VideoPlayerMediaType) => boolean = () => false;

  setQualityFor: (mediaType: VideoPlayerMediaType, index: number) => void = () => {};

  setAutoSwitchQualityFor: (mediaType: VideoPlayerMediaType, value: boolean) => void = () => {};

  pause: () => void = () => {
    const { videoElement } = this;

    videoElement.pause();
  };

  play: () => Promise<void> = () => {
    const { videoElement } = this;

    return videoElement.play();
  };

  isPaused: () => boolean = () => {
    const { videoElement } = this;

    return videoElement.paused ?? false;
  };

  setVolume: (volume: number) => void = (volume) => {
    const { videoElement } = this;

    videoElement.volume = volume;
  };

  isMuted: () => boolean = () => {
    const { videoElement } = this;

    return videoElement.muted ?? false;
  };

  setMute: (mute: boolean) => void = (mute) => {
    const { videoElement } = this;

    videoElement.muted = mute;
  };

  getPlayheadTime: () => number = () => {
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      return 0;
    }

    return shakaPlayer.getPlayheadTimeAsDate()?.getTime() ?? 0;
  };

  /*
   * Seek in a live stream
   *   value: positive or negative number (in seconds), representing the delta between current time and target time
   */
  liveSeek: (value: number) => void = (value) => {
    const {
      contentType,
      shakaPlayer,
      videoElement,
      videoElement: { currentTime },
    } = this;

    if (!shakaPlayer || contentType !== ContentType.Live) {
      return;
    }

    const { end, start } = shakaPlayer.seekRange();
    videoElement.currentTime = getBoundedValue(currentTime + value, start, end);
  };

  goBackToLive: () => void = () => {
    const { contentType, shakaPlayer } = this;

    if (!shakaPlayer || contentType !== ContentType.Live) {
      return;
    }

    shakaPlayer.goToLive();
  };

  /*
   * Seek in a static stream (or a live stream in the case of an in-progress recording)
   *   value (in-progress recording): positive number (in seconds), representing the delta between start time and target time
   *   value (catchup, recording or VOD): positive number (in seconds), representing the target time
   */
  seek: (value: number) => void = (value) => {
    const { contentType, shakaPlayer, videoElement } = this;

    if (!shakaPlayer) {
      return;
    }

    if (contentType === ContentType.Static) {
      // Static content: catchup, finished recording, VOD
      videoElement.currentTime = value;
    } else if (contentType === ContentType.LiveRecording) {
      // Live recording: only used for recordings still in progress
      const { start } = shakaPlayer.seekRange();
      videoElement.currentTime = start + value;
    }
  };

  /*
   * Only used during loading stage
   */
  setStartTime: (value: number) => void = (value) => {
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      return;
    }

    shakaPlayer.updateStartTime(value);
  };

  download: (downloadOperationId: string, metadata: ShakaOfflineExtraMetadata) => Promise<void> = async (downloadOperationId, metadata) => {
    const { initData, shakaPlayer } = this;

    if (!initData || !shakaPlayer) {
      return;
    }

    const updateDownloadProgress = generateUpdateDownloadProgress(downloadOperationId, metadata.title);

    try {
      await ShakaStorage.downloadContent(shakaPlayer, downloadOperationId, initData.url, metadata, selectDownloadTracks, updateDownloadProgress);
      updateDownloadProgress(null, 1);
    } catch (error) {
      const { code } = error;
      if (code === ShakaErrors.OperationAborted) {
        Messenger.emit(MessengerEvents.NOTIFY_WARNING, 'Téléchargement interrompu');
        return;
      }

      logError(error);
      updateDownloadProgress(null, -1);
    }
  };

  setCurrentTrack: (mediaInfo: VideoPlayerMediaInfo) => void = (mediaInfo) => {
    const { shakaPlayer, streamInfoUpdatedCallback } = this;
    const { index, type } = mediaInfo;

    if (!shakaPlayer) {
      return;
    }

    const typeEnum = VideoPlayerMediaType.cast(type.toLowerCase());

    if (typeEnum === VideoPlayerMediaType.Audio) {
      // Audio track
      const audioTracks = shakaPlayer.getAudioTracks();
      shakaPlayer.selectAudioTrack(audioTracks[index]);
    } else if (typeEnum === VideoPlayerMediaType.Subtitles) {
      // Subtitles track
      const textTracks = shakaPlayer.getTextTracks();
      shakaPlayer.selectTextTrack(textTracks[index]);
    }

    streamInfoUpdatedCallback();
  };

  unsetCurrentTrack: (mediaType: VideoPlayerMediaType) => void = (mediaType) => {
    const { shakaPlayer, streamInfoUpdatedCallback } = this;

    if (!shakaPlayer) {
      return;
    }

    switch (mediaType) {
      case VideoPlayerMediaType.Audio:
        // Audio track
        shakaPlayer.audioTrack = -1;
        this.selectedAudioId = null;
        break;

      case VideoPlayerMediaType.Subtitles:
        // Subtitles track
        this.selectedTextId = null;
        shakaPlayer.selectTextTrack(null);
        streamInfoUpdatedCallback();
        break;

      default:
    }
  };

  // Current audio track is found amongst all variants via getVariantTracks() but the selection is done through getAudioTracks(), which returns a smaller list
  getAudioTrackIndex: (shakaPlayer: typeof shaka.Player, audioTrack: IndexedShakaTrack) => number = (shakaPlayer, audioTrack) => {
    const {
      track: { label, language, roles },
    } = audioTrack;
    const role = roles && roles.length > 0 ? roles[0] : '';
    const languageAndRoles = shakaPlayer.getAudioTracks();

    for (let i = 0; i < languageAndRoles.length; ++i) {
      const {
        [i]: { label: currLabel, language: currLanguage, roles: currRoles },
      } = languageAndRoles;

      const [currRole = ''] = currRoles;

      if (((typeof label !== 'string' && typeof currLabel !== 'string') || (typeof label === 'string' && currLabel === label)) && currLanguage === language && currRole === role) {
        return i;
      }
    }

    return -1;
  };

  getActiveTrack: (tracks: ShakaTrackList) => IndexedShakaTrack | null = (tracks) => {
    if (!tracks) {
      return null;
    }

    for (let index = 0; index < tracks.length; index += 1) {
      const track = tracks[index];
      const { active, language } = track;

      if (active && language !== '') {
        return {
          index,
          track,
        };
      }
    }

    return null;
  };

  getActiveAudioTrack: (shakaPlayer: typeof shaka.Player) => IndexedShakaTrack | null = (shakaPlayer) => this.getActiveTrack(shakaPlayer.getVariantTracks());

  getActiveTextTrack: (shakaPlayer: typeof shaka.Player) => IndexedShakaTrack | null = (shakaPlayer) => {
    const { selectedTextId } = this;

    if (selectedTextId === null) {
      // No text track is currently selected (either none is active or one is active but subtitles are hidden)
      return null;
    }

    return this.getActiveTrack(shakaPlayer.getTextTracks());
  };

  getCurrentTrackFor: (mediaType: VideoPlayerMediaType) => VideoPlayerMediaInfo | null = (mediaType) => {
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      return null;
    }

    switch (mediaType) {
      case VideoPlayerMediaType.Audio: {
        const indexedTrack = this.getActiveAudioTrack(shakaPlayer);
        if (indexedTrack) {
          // Index returned by getActiveAudioTrack() is the index among all variants, but we want the index in the list of tracks as seen by user
          const index = this.getAudioTrackIndex(shakaPlayer, indexedTrack);
          const { track } = indexedTrack;
          return this.mapAudioShakaTrack(track, index);
        }
        return null;
      }

      case VideoPlayerMediaType.Subtitles: {
        const indexedTrack = this.getActiveTextTrack(shakaPlayer);
        if (indexedTrack) {
          const { index, track } = indexedTrack;
          return this.mapTextTrack(track, index);
        }
        return null;
      }

      case VideoPlayerMediaType.Video:
        return null;

      // No default
    }
  };

  mapAudioShakaTrack: (track: ShakaTrack, index: number) => VideoPlayerMediaInfo | null = (track, index) => {
    const { kind, label, language, roles } = track;

    if (!language) {
      return null;
    }

    const role = roles && roles.length > 0 ? roles[0] : '';

    return {
      index,
      kind: kind ?? role,
      lang: language,
      name: label ?? undefined,
      type: VideoPlayerMediaType.Audio as string,
    };
  };

  mapAudioShakaLanguageRole: (track: ShakaAudioLanguageRole, index: number) => VideoPlayerMediaInfo | null = (track, index) => {
    const { label, language, roles } = track;

    if (!language) {
      return null;
    }

    return {
      index,
      kind: roles[0],
      lang: language,
      name: label ?? undefined,
      type: VideoPlayerMediaType.Audio as string,
    };
  };

  mapTextTrack: (track: ShakaTrack, index: number) => VideoPlayerMediaInfo = (track, index) => {
    const { kind, label, language } = track;

    return {
      index,
      kind: isHearingImpairedSubtitlesTrack(track) ? CAPTIONS : (kind ?? undefined),
      lang: language,
      name: label ?? undefined,
      type: VideoPlayerMediaType.Subtitles as string,
    };
  };

  mapAudioTracks: (tracks: Array<ShakaAudioLanguageRole>) => Array<VideoPlayerMediaInfo> = (tracks) => {
    if (!tracks) {
      return [];
    }

    const result = [];
    for (let i = 0; i < tracks.length; i += 1) {
      const track = this.mapAudioShakaLanguageRole(tracks[i], i);

      if (track) {
        result.push(track);
      }
    }
    return result;
  };

  mapTextTracks: (textTracks: Array<ShakaTrack>) => Array<VideoPlayerMediaInfo> = (textTracks) => {
    const result = [];
    for (let i = 0; i < textTracks.length; i += 1) {
      const { [i]: track } = textTracks;
      const { language } = track;

      if (language !== '') {
        result.push(this.mapTextTrack(track, i));
      }
    }
    return result;
  };

  getTracksFor: (mediaType: VideoPlayerMediaType) => Array<VideoPlayerMediaInfo> = (mediaType) => {
    const { shakaPlayer } = this;

    if (!shakaPlayer) {
      return [];
    }

    switch (mediaType) {
      case VideoPlayerMediaType.Audio:
        return this.mapAudioTracks(shakaPlayer.getAudioTracks());

      case VideoPlayerMediaType.Subtitles:
        return this.mapTextTracks(shakaPlayer.getTextTracks());

      case VideoPlayerMediaType.Video:
        return [];

      // No default
    }
  };

  getMetrics: () => VideoPlayerLiveMetrics | null = () => {
    const { hasVUDrmToken, selectedAudioId, selectedTextId, selectedVideoId, shakaPlayer, tracks } = this;

    if (!shakaPlayer) {
      return null;
    }

    return {
      shakaMetrics: {
        bufferedInfo: shakaPlayer.getBufferedInfo(),
        bufferFullness: shakaPlayer.getBufferFullness(),
        drmInfo: {
          ...shakaPlayer.drmInfo(),
          hasVUDrmToken,
        },
        selectedTrackIds: {
          audio: selectedAudioId,
          text: selectedTextId,
          video: selectedVideoId,
        },
        stats: shakaPlayer.getStats(),
        tracks: cloneAllTracks(tracks),
      },
    };
  };
}

export default PlayerShaka;
